<template>
  <div class="ice-pail-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$导体内部净电荷 = 0$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <!-- Stand & Electroscope Base -->
          <rect x="250" y="160" width="80" height="40" fill="#1e293b" stroke="#334155" rx="4" />
          <line x1="290" y1="160" x2="290" y2="100" stroke="#94a3b8" stroke-width="3" />
          <line x1="290" y1="100" x2="230" y2="100" stroke="#94a3b8" stroke-width="3" />

          <!-- The Hollow Metal Pail -->
          <path d="M 170,80 L 170,180 Q 170,190 200,190 Q 230,190 230,180 L 230,80" fill="none" stroke="#64748b" stroke-width="6" />

          <!-- Connecting Wire to Electroscope -->
          <path d="M 233,140 Q 260,140 290,140" fill="none" stroke="#fcd34d" stroke-width="2" />
          
          <!-- Electroscope Leaves -->
          <g transform="translate(290, 180)">
             <!-- Central rod -->
             <line x1="0" y1="-20" x2="0" y2="10" stroke="#fcd34d" stroke-width="2" />
             <!-- Leaves -->
             <line x1="0" y1="-10" :x2="leafDeflectionX" :y2="leafDeflectionY" stroke="#fcd34d" stroke-width="2" stroke-linecap="round" />
             <line x1="0" y1="-10" :x2="-leafDeflectionX" :y2="leafDeflectionY" stroke="#fcd34d" stroke-width="2" stroke-linecap="round" />
             <circle cx="0" cy="-20" r="4" fill="#fcd34d" />
             <text x="0" y="25" fill="#94a3b8" font-size="9" text-anchor="middle">外接静电计</text>
          </g>

          <!-- Charges on the outside of the pail -->
          <g v-if="inducedCharge > 0">
             <text x="155" y="100" fill="#ef4444" font-size="12" font-weight="bold" opacity="0.8">+</text>
             <text x="155" y="150" fill="#ef4444" font-size="12" font-weight="bold" opacity="0.8">+</text>
             <text x="240" y="100" fill="#ef4444" font-size="12" font-weight="bold" opacity="0.8">+</text>
             <text x="240" y="150" fill="#ef4444" font-size="12" font-weight="bold" opacity="0.8">+</text>
          </g>
          
          <!-- Charges on the inside of the pail (induced negative if sphere is inside but not touched) -->
          <g v-if="inducedCharge > 0 && !hasTouched">
             <text x="175" y="130" fill="#06b6d4" font-size="14" font-weight="bold" opacity="0.8">-</text>
             <text x="220" y="130" fill="#06b6d4" font-size="14" font-weight="bold" opacity="0.8">-</text>
          </g>

          <!-- The Charged Metal Sphere -->
          <g :transform="`translate(200, ${sphereY})`">
             <!-- Insulating handle -->
             <line x1="0" y1="-80" x2="0" y2="-15" stroke="#475569" stroke-width="4" stroke-linecap="round" />
             <!-- The sphere -->
             <circle r="15" fill="#334155" stroke="#94a3b8" stroke-width="2" />
             <!-- Charge on sphere -->
             <g v-if="!hasTouched">
                <text x="-5" y="5" fill="#ef4444" font-size="16" font-weight="bold">+</text>
             </g>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>静电计张角</label>
              <span class="val">{{ (leafAngle * 180 / Math.PI).toFixed(1) }} <small>°</small></span>
           </div>
           <div class="hud-item orange">
              <label>电荷状态</label>
              <span class="val" :style="{color: hasTouched ? '#22c55e' : '#f8fafc'}">
                 {{ hasTouched ? '已发生全电荷转移' : (sphereY > 80 ? '内表面感应起电中' : '带电球在筒外') }}
              </span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">手持绝缘柄将带电小球探入并接触冰桶</label>
                    <el-slider v-model="sphereY" :min="30" :max="180" :step="1" size="small"></el-slider>
                 </div>
                 <div>
                    <el-button type="primary" size="small" plain @click="resetExperiment" icon="el-icon-refresh">取出并重置实验</el-button>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>当带正电的小球完全进入冰桶（未接触）时，由于<strong>静电感应</strong>，内表面产生等量负电荷，外表面产生等量正电荷。此时静电计张角达到最大。由于构成了一个封闭的高斯面，不论球在桶内何处移动，只要不拿出，外部张角便保持不变。当小球接触底部时，正负电荷中和，小球身上的所有净电荷瞬间全部转移至冰桶极外层表面，小球完全变为电中性！此实验证明了静电平衡下导体内部全无净电荷。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FaradayIcePailViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      sphereY: 30,
      hasTouched: false
    }
  },
  computed: {
    // 0 to 1 coupling factor based on depth into the pail
    inducedCharge() {
       if (this.hasTouched) return 1.0; 
       if (this.sphereY <= 60) return 0;
       if (this.sphereY >= 140) return 1.0;
       return (this.sphereY - 60) / 80;
    },
    leafAngle() {
       // max deflection is pi/6 (30 degrees)
       return this.inducedCharge * (Math.PI / 6);
    },
    leafDeflectionX() {
       return Math.sin(this.leafAngle) * 20;
    },
    leafDeflectionY() {
       return Math.cos(this.leafAngle) * 20 - 10;
    }
  },
  watch: {
    sphereY(val) {
       // 175 is touching the bottom
       if (val >= 170 && !this.hasTouched) {
          this.hasTouched = true;
       }
    }
  },
  methods: {
    resetExperiment() {
       this.sphereY = 30;
       this.hasTouched = false;
    }
  }
}
</script>

<style scoped lang="scss">
.ice-pail-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
